import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import { toast } from "react-toastify";
import { SaveCells } from "Services/PGXLis/ResultFilePreConfiguration";
import PermissionComponent, {
  getPermissionDisplayName,
} from "Shared/Common/Permissions/PermissionComponent";
import useLang from "Shared/hooks/useLanguage";
import { LoaderIcon } from "Shared/Icons";

interface ExpandProps {
  innerRow: any[];
  setOpen: (value: boolean) => void;
  loadGridData: (refresh: boolean, reset: boolean) => void;
}

const Expand = ({ innerRow, setOpen, loadGridData }: ExpandProps) => {
  const { t } = useLang();

  const [request, setRequest] = useState(false);
  const [data, setData] = useState<any[]>(innerRow);

  const updateRowById = (id: number, updater: (row: any) => any) => {
    setData((prev) => prev.map((row) => (row.id === id ? updater(row) : row)));
  };

  const handleToggleBooleanField = (
    id: number,
    field: "isActive" | "isActiveResult",
    currentValue: boolean
  ) => {
    updateRowById(id, (row) => {
      if (field === "isActive") {
        const newIsActive = !currentValue;

        return {
          ...row,
          isActive: newIsActive,
          // if isActive becomes false, force isActiveResult to false
          isActiveResult: newIsActive ? row.isActiveResult : false,
        };
      }

      return {
        ...row,
        [field]: !currentValue,
      };
    });
  };

  const handleChangeCustomCellName = (id: number, value: string) => {
    updateRowById(id, (row) => ({
      ...row,
      customCellName: value,
    }));
  };

  const handleChangeCustomCellOrder = (id: number, value: string) => {
    updateRowById(id, (row) => ({
      ...row,
      customCellOrder: parseInt(value || "0", 10),
    }));
  };

  const handleSubmit = async () => {
    setRequest(true);
    try {
      const res = await SaveCells(data);
      if (res?.data?.statusCode === 200) {
        toast.success(res.data.message);
        setOpen(false);
        loadGridData(false, false);
      } else if (res?.data?.statusCode === 400) {
        toast.success(res.data.message);
      }
    } catch (error) {
      console.trace(error);
    } finally {
      setRequest(false);
    }
  };

  return (
    <div className="py-2">
      <div className="d-flex align-items-center gap-2 gap-lg-3 mb-2">
        <button
          id="IDTemplateSettingExpandCancel"
          className="btn btn-secondary btn-sm btn-secondary--icon fw-bold py-2 rounded-3"
          onClick={() => setOpen(false)}
        >
          {t("Cancel")}
        </button>

        <PermissionComponent
          moduleName="PGX LIS"
          pageName="Result File Pre-configuration"
          permissionIdentifier="Save"
        >
          {({ pageName, moduleName, permissionIdentifier }) => (
            <button
              id="IDTemplateSettingExpandSave"
              className="btn btn-primary btn-sm fw-500 py-2 rounded-3"
              onClick={handleSubmit}
            >
              {request && <LoaderIcon />}
              {getPermissionDisplayName(
                pageName,
                moduleName,
                permissionIdentifier,
                "Save"
              )}
            </button>
          )}
        </PermissionComponent>
      </div>

      <div className="table_bordered overflow-hidden">
        <TableContainer className="shadow-none">
          <Table
            aria-label="sticky table collapsible"
            className="table table-cutome-expend table-bordered table-sticky-header plate-mapping-table table-head-2-bg table-bg table-head-custom table-vertical-center border-0 mb-0"
          >
            <TableHead className="h-30px">
              <TableRow>
                <TableCell className="min-w-200px w-200px">
                  {t("System Cell Name")}
                </TableCell>
                <TableCell className="min-w-200px w-200px">
                  {t("Custom Cell Name")}
                </TableCell>
                <TableCell className="min-w-200px w-200px">
                  {t("Custom Cell ID")}
                </TableCell>
                <TableCell className="min-w-50px w-50px">
                  {t("Exclude/Include")}
                </TableCell>
                <TableCell className="min-w-50px w-50px">
                  {t("Exclude/Include Result")}
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {data.map((row) => (
                <TableRow key={row.id}>
                  <TableCell
                    id={`PGXTemplateSettingExpandSystemCellName_${row.id}`}
                  >
                    {row.systemCellName}
                  </TableCell>

                  <TableCell>
                    <input
                      id={`PGXTemplateSettingExpandCustomCellName_${row.id}`}
                      type="text"
                      className="form-control bg-white h-25px rounded-2 py-0 fs-8 fw-500 px-2"
                      placeholder={t("Custom Cell Name")}
                      value={row.customCellName}
                      onChange={(e) =>
                        handleChangeCustomCellName(row.id, e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell>
                    <input
                      id={`PGXTemplateSettingExpandCustomCellOrder_${row.id}`}
                      type="text"
                      className="form-control bg-white h-25px rounded-2 py-0 fs-8 fw-500 px-2"
                      placeholder={t("Custom Cell Order")}
                      value={row.customCellOrder}
                      onChange={(e) =>
                        handleChangeCustomCellOrder(row.id, e.target.value)
                      }
                    />
                  </TableCell>

                  <TableCell className="text-center">
                    <div className="d-flex justify-content-center">
                      <div className="form-check form-switch">
                        <input
                          id={`PGXTemplateSettingExpandIsActive_${row.id}`}
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          role="switch"
                          checked={row.isActive}
                          onChange={() =>
                            handleToggleBooleanField(
                              row.id,
                              "isActive",
                              row.isActive
                            )
                          }
                        />
                      </div>
                    </div>
                  </TableCell>

                  <TableCell className="text-center">
                    <div className="d-flex justify-content-center">
                      <div className="form-check form-switch">
                        <input
                          id={`PGXTemplateSettingExpandIsActiveResult_${row.id}`}
                          className="form-check-input cursor-pointer"
                          type="checkbox"
                          role="switch"
                          checked={row.isActiveResult}
                          disabled={!row.isActive}
                          onChange={() =>
                            handleToggleBooleanField(
                              row.id,
                              "isActiveResult",
                              row.isActiveResult
                            )
                          }
                        />
                      </div>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </div>
  );
};

export default Expand;
